import esriId from '@arcgis/core/identity/IdentityManager.js';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PratusCornerLogo from 'images/branding/pratus-corner-logo.png';
import PratusLogoIconMono from 'assets/images/pratus__logoIcon--mono.svg?react';
import Dropdown from 'react-bootstrap/Dropdown';
import StylishNewSelect, {
  OptionType,
} from '../DesignSystems/New/StylishNewSelect';
import { NavLink, useNavigate, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateCurrentIncident,
  updateCurrentlySelectedGroup,
} from '../../actions/diceActions';
import { useChatSidebar } from '../GlobalChatSidebar/ChatSidebarProvider';
import { fetchExerciseWorkspaces } from '../../actions/workspaceActions';
import { getInitials } from '../../helpers/intials';
import SidebarNav from '../sidebarNav/SidebarNav';
import { useSidebarNavContext } from '../sidebarNav/SidebarNavProvider';
import { useClickAway } from 'react-use';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useBrowserAuth } from '../../providers/BrowserAuthProvider';
import { useStreamlinePlatformFlag } from 'utils/featureFlags';
import { useIsDev } from '../../hooks/useIsDev';
import { PLATFORM_VERSION } from '../../environment';
import { useHealthCheck } from 'hooks/useHealthCheck';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { useAppEvents } from '../../providers/AppEventsProvider';
import { PRATUS_KNOWLEDGE_URL } from 'assets/data/HelpContent';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useIncidentInfo } from '../IAP/hooks/incidentInfoHooks';
import { PratusHeaderNavTabs } from './PratusHeaderNavTabs';
import { PeriodButtonGroup, ToggleMapButton } from './PeriodButtonGroup';
import { usePeriods, useIncident } from 'components/IAP/hooks/incidentHooks';
import { useIsCommandCenter } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/isCommandCenter';
import { useUserPositions } from 'hooks/incident/useUserPosition';
import { useUscgMapContext } from '../MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { EscalateResponseButton } from './EscalateResponseButton';

dayjs.extend(utc);

const PratusHeader: React.FC = () => {
  const { logout } = useBrowserAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isDev = useIsDev();
  const { data: healthCheck } = useHealthCheck(isDev);

  const { needsRefresh } = useAppEvents();

  const isCommandCenter = useIsCommandCenter();
  const [manualTooltipToggle, setManualTooltipToggle] = useState(false);
  const showTooltip = needsRefresh || manualTooltipToggle;

  const streamlinePlatform = useStreamlinePlatformFlag();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const toggleRef = React.createRef<HTMLButtonElement>();

  const { toggle, isOpen } = useSidebarNavContext();

  const [showDropdown, setShowDropdown] = useState(false);

  const user = useSelector((state: any) => {
    // TODO: obviously this should just be done in the reducer.
    // BEWARE using this user in dependency arrays. depend on a specific property.
    return {
      ...(state.app.user as User),
      ...(state.prepare.user as PrepareUser),
    };
  });

  const featureFlags = useSelector((state: any) => {
    return state.app.featureFlags as string[];
  });

  const currentGroup = useSelector((state: any) => {
    return state.app.currentlySelectedGroup as Group;
  });

  const groups = useSelector((state: any) => {
    return state.app.groups as Group[];
  });

  const incidents = useSelector((state: any) => {
    return state.app.incidents as IncidentAugmented[];
  });

  // Read the routed incident (same source the hamburger workspace selector and the IAP hooks use) so the
  // navbar title always matches the workspace the user is in — including AI-created incidents not yet in the
  // Redux incidents list (useIncident fetches by id). Avoids the header/hamburger divergence without adding a
  // competing writer to Redux `currentIncident`.
  const { data: currentIncident } = useIncident();
  const { data: periods } = usePeriods(!isCommandCenter);
  const isEscalatedIncident = (() => {
    if (isCommandCenter) return false;
    if (currentIncident?.escalated_at) return true;
    if (!periods) return false;

    return periods.some((p) => {
      const periodNumber = p.period_number ?? 0;

      return p.incident_id === currentIncident?.id && periodNumber > 0;
    });
  })();

  const { isVisible: isChatSidebarVisible, toggle: toggleChatSidebar } =
    useChatSidebar();

  const showIncidentInfo = location.pathname.startsWith('/iap');
  const showHighSecurityBanner = featureFlags.includes('HIGH_SECURITY');
  const { data: incidentInfo } = useIncidentInfo(showIncidentInfo);

  const positions = useUserPositions();

  const { isMapVisible } = useUscgMapContext();

  const groupOptions = useMemo<OptionType[]>(
    () =>
      groups
        .filter((group) => !group.is_closed)
        .map((group) => ({
          label: group.group_name,
          value: group.group_guid,
        })),
    [groups]
  );

  useClickAway(dropdownRef, () => {
    setShowDropdown(false);
  });

  useEffect(() => {
    if (currentGroup.group_guid) {
      dispatch(
        fetchExerciseWorkspaces(user.user_guid, currentGroup.group_guid)
      );
    }
  }, [dispatch, user.user_guid, currentGroup.group_guid]);

  const organizationChangeHandler = (orgGuid: string) => {
    if (orgGuid !== currentGroup?.group_guid) {
      dispatch(
        updateCurrentlySelectedGroup(
          groups.find((group) => group.group_guid === orgGuid)
        )
      );
      const firstIncident = incidents.find(
        (incident) => incident.group_guid === orgGuid
      );
      dispatch(updateCurrentIncident(firstIncident));
      navigate(`/hub`);
    }
  };

  const handleLogout = async () => {
    esriId.destroyCredentials();
    await logout();
  };

  return (
    <>
      <header id="app-header" className="position-sticky zIndex--3 top-0">
        {showHighSecurityBanner && (
          <div
            className="d-flex justify-content-center gap-1"
            style={{
              backgroundColor: 'var(--success-700)',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 4,
            }}
          >
            <span style={{ fontSize: '.7rem' }}>CUI Limited Pre-Release </span>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="pre-release-tooltip">
                  PRATUS is in Initial Operating Capability. Full Operating
                  requirements will be defined based on your feature requests
                  and usage throughout the summer 2026 special events.
                </Tooltip>
              }
            >
              <span className="d-inline-flex align-items-center">
                <i
                  className="material-symbols-outlined"
                  style={{ fontSize: '.8rem' }}
                >
                  info
                </i>
              </span>
            </OverlayTrigger>
          </div>
        )}
        <div className="bg-bglight d-flex align-items-center">
          {/* Logo + hamburger — direct flex child, always protected */}
          <div className="d-flex logo-container align-items-center me-3 flex-shrink-0">
            <img src={PratusCornerLogo} alt="Pratus Logo" />
            <button
              className="button--icon sml ms-4 border-0 p-3"
              onClick={() => toggle()}
              ref={toggleRef}
            >
              {isOpen ? (
                <i className="material-symbols-outlined">menu_open</i>
              ) : (
                <i className="material-symbols-outlined">menu</i>
              )}
            </button>
          </div>

          {/* Incident info — fills remaining space between logo and right buttons */}
          <div className="d-flex align-items-center header-incident-info flex-grow-1 gap-1 overflow-hidden">
            {showIncidentInfo && currentIncident && (
              <>
                {currentIncident.name && (
                  <p className="text-truncate header-incident-name mb-0 text-white">
                    {currentIncident.name}
                  </p>
                )}
                <div className="header-position-badge d-none d-lg-flex align-items-center">
                  My Positions: {positions.join(', ')}
                </div>
                {incidentInfo?.incidentLocation && (
                  <p className="header-incident-location d-none d-lg-block mb-0 text-white">
                    Location: {incidentInfo.incidentLocation}
                  </p>
                )}
                {!isMapVisible && (
                  <div className="d-none d-md-flex">
                    <PratusHeaderNavTabs />
                  </div>
                )}
              </>
            )}
          </div>

          {/* Right buttons — can shrink to zero and clip; avatar is protected separately */}
          <div className="d-flex align-items-center header-right-buttons">
            {showIncidentInfo && !isCommandCenter && <PeriodButtonGroup />}
            {isCommandCenter && (
              <div className="d-none d-md-flex">
                <ToggleMapButton />
              </div>
            )}
            <div className="d-flex help-icon align-items-center gap-2 ps-2">
              <OverlayTrigger
                placement="bottom"
                show={showTooltip}
                overlay={
                  <Tooltip className="warning-tooltip">
                    <div className="tooltip-title">Reload Browser</div>
                    <div className="tooltip-body">
                      Changes have been made to the {currentGroup?.group_name}{' '}
                      group. Reload your browser to see updates.
                    </div>
                  </Tooltip>
                }
              >
                <StylishNewButton
                  style={{ display: needsRefresh ? 'block' : 'none' }}
                  className="button button--link"
                  onClick={() => setManualTooltipToggle(!manualTooltipToggle)}
                >
                  <i className="material-symbols-outlined pulsing-warning-icon">
                    warning
                  </i>
                </StylishNewButton>
              </OverlayTrigger>
              {showIncidentInfo && !isEscalatedIncident && (
                <EscalateResponseButton />
              )}
              {featureFlags.includes('OFFLINE_ENVIRONMENT') ? (
                <NavLink to="/knowledge_base">
                  <i className="material-symbols-outlined color-primary-hover">
                    help
                  </i>
                </NavLink>
              ) : (
                <a
                  className="text-white"
                  href={PRATUS_KNOWLEDGE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="material-symbols-outlined color-primary-hover">
                    help
                  </i>
                </a>
              )}
              <StylishNewButton
                className={`d-none d-md-flex button ${isChatSidebarVisible ? 'button--primary' : 'button--ai button--ai-glow'} ms-2 text-nowrap`}
                onClick={toggleChatSidebar}
                title="PRATUS AI Agent"
              >
                PRATUS AI
              </StylishNewButton>
              <StylishNewButton
                className={`d-flex d-md-none button ${isChatSidebarVisible ? 'button--primary' : 'button--ai button--ai-glow'} ms-2 gap-1`}
                onClick={toggleChatSidebar}
                title="PRATUS AI Agent"
              >
                <PratusLogoIconMono style={{ height: '1em', width: 'auto' }} />
                AI
              </StylishNewButton>
            </div>
          </div>

          {/* Profile dropdown — direct flex child, always protected like the logo */}
          <Dropdown
            className="dropdown__pratus flex-shrink-0"
            ref={dropdownRef}
            show={showDropdown}
          >
            <Dropdown.Toggle
              className="button bg-0 border-0"
              onClick={() => setShowDropdown(!showDropdown)}
            >
              <div className="avatar-initials sml">
                {getInitials(
                  user.profile_settings?.userName ?? user.email_address
                )}
              </div>
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              <div className="section-profile">
                <div className="d-flex align-items-center">
                  <div className="avatar-initials sml bg-bglight">
                    {getInitials(
                      user.profile_settings?.userName ?? user.email_address
                    )}
                  </div>
                  <div className="d-flex flex-column justify-content-start ps-2 text-white">
                    <span>{user.profile_settings?.userName}</span>
                    <span className="tw-text-xs">{user.email_address}</span>
                  </div>
                </div>
              </div>
              <Dropdown.Divider />
              <div className="section-select">
                <label className="form-label">Sponsor Organization</label>
                <StylishNewSelect
                  classes={'secondary sml'}
                  options={groupOptions}
                  value={
                    currentGroup?.group_guid
                      ? {
                          value: currentGroup.group_guid,
                          label: currentGroup.group_name,
                        }
                      : ''
                  }
                  placeholder="Select a Organization"
                  onChange={(e) => {
                    organizationChangeHandler(e.value);
                  }}
                />
              </div>
              <div className="section-pages">
                <Dropdown.Item
                  as={NavLink}
                  to="/profile"
                  onClick={() => setShowDropdown(false)}
                >
                  Profile
                </Dropdown.Item>
                {streamlinePlatform ? null : (
                  <Dropdown.Item
                    as={NavLink}
                    to="/activity"
                    onClick={() => setShowDropdown(false)}
                  >
                    Activity Log
                  </Dropdown.Item>
                )}
                <Dropdown.Item
                  as="a"
                  href={window.location.origin + '/privacy.html'}
                  target="_blank"
                  onClick={() => {
                    setShowDropdown(false);
                  }}
                >
                  Privacy Policy
                </Dropdown.Item>
                <Dropdown.Item
                  as="a"
                  href={window.location.origin + '/tos.html'}
                  target="_blank"
                  onClick={() => {
                    setShowDropdown(false);
                  }}
                >
                  Terms of Service
                </Dropdown.Item>
                {isDev ? (
                  <>
                    <Dropdown.Item
                      as={NavLink}
                      to="/subscription_management"
                      onClick={() => setShowDropdown(false)}
                    >
                      Subscription Management
                    </Dropdown.Item>
                    <Dropdown.Item
                      as={NavLink}
                      to="/feature_flags"
                      onClick={() => setShowDropdown(false)}
                    >
                      Feature Flags
                    </Dropdown.Item>
                    <Dropdown.Item
                      as={NavLink}
                      to="/ai_usages"
                      onClick={() => setShowDropdown(false)}
                    >
                      AI Usages
                    </Dropdown.Item>
                    <div className="version mt-2">DICE: {PLATFORM_VERSION}</div>
                    {healthCheck?.status ? (
                      <div className="version">
                        COMS: {healthCheck?.platform}
                      </div>
                    ) : (
                      <div className="version">COMS: Not Healthy</div>
                    )}
                  </>
                ) : null}
                <Dropdown.Divider className="mb-0" />
                <Dropdown.Item
                  onClick={() => handleLogout()}
                  className="pointer-cursor d-flex justify-content-center align-items-center"
                >
                  <SharedIcon iconName="logout" />
                  <span className="ps-2">Log Out</span>
                </Dropdown.Item>
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </header>
      <SidebarNav toggleRef={toggleRef} />
      {isOpen && <div className="header-sidebar-overlay"></div>}
    </>
  );
};

export default PratusHeader;
